import type { AvatarId } from './ids';

/** Login methods. Only the ones the server has configured are ever offered (`/api/config` → `accounts`). */
export const AUTH_PROVIDER_IDS = ['email', 'google', 'facebook', 'discord', 'gmg'] as const;
export type AuthProviderId = (typeof AUTH_PROVIDER_IDS)[number];

/** Friend codes: 8 characters from an alphabet without look-alikes (no 0/O, 1/I/L), shown as ABCD-EFGH. */
export const FRIEND_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const FRIEND_CODE_LENGTH = 8;
export const normalizeFriendCode = (raw: string) => raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
export const formatFriendCode = (code: string) => (code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code);

/** Party invites from a friend expire after this long. */
export const INVITE_TTL_MS = 5 * 60_000;

export type PresenceStatus = 'online' | 'in_party' | 'offline';
/** What a friend sees about you. Never a party code: friends join through an invite. */
export interface Presence {
  status: PresenceStatus;
  /** Game name when in a party. */
  game?: string;
  stage?: 'lobby' | 'playing';
}

export interface PersonView {
  accountId: string;
  displayName: string;
  avatar: AvatarId;
}

export interface FriendView extends PersonView {
  presence: Presence;
}

export interface RecentPlayerView {
  accountId: string;
  /** The nickname and character they used in the party you shared (not their account name). */
  nickname: string;
  avatar: AvatarId;
  seenAt: number;
  relation: 'none' | 'friend' | 'outgoing' | 'incoming';
}

export interface InviteView {
  id: string;
  from: PersonView;
  gameName: string;
  expiresAt: number;
}

export interface LoginView {
  provider: AuthProviderId;
  /** Your own email for that login, when the provider shared one. */
  email: string | null;
}

export interface MyAccountView {
  accountId: string;
  displayName: string;
  avatar: AvatarId;
  friendCode: string;
  logins: LoginView[];
}

export interface SocialSnapshot {
  me: MyAccountView;
  friends: FriendView[];
  incoming: PersonView[];
  outgoing: PersonView[];
  blocked: PersonView[];
  recent: RecentPlayerView[];
  invites: InviteView[];
}

/** Server → client social messages (part of ServerMessage). */
export type SocialServerMessage =
  /** Signed-out connections get `snapshot: null`. */
  | { type: 'social'; snapshot: SocialSnapshot | null }
  /** A friend's presence changed (only ever sent to accepted friends). */
  | { type: 'presence'; accountId: string; presence: Presence }
  | { type: 'invite'; invite: InviteView }
  | { type: 'invite_closed'; id: string; reason: 'expired' | 'answered' | 'cancelled' };

/** `/api/config` → `accounts`: null when no login method is configured (sign-in is then hidden). */
export interface AccountsConfig {
  providers: { id: AuthProviderId; name: string }[];
}
